/**
 * AFORDA colourway image preview.
 * Hover/focus temporarily previews a Shopify-hosted colourway image on top of
 * the active product gallery slide. Does not change product, variant, price or URL.
 * Only applies to the product page where colour swatches are provided.
 */
(() => {
  const swatchSelector = '.aforda-listing-colourways__item[data-aforda-preview-src], .aforda-colourway-picker__item[data-aforda-preview-src]';
  const activeState = { swatch: null, root: null, overlay: null, token: 0, initialName: null };

  // Liquid image_url may emit protocol-relative paths (//cdn.shopify.com/...),
  // full HTTPS URLs, or Shopify's same-origin /cdn/shop/ image proxy.
  // Normalize these safely instead of rejecting valid live-product images.
  function validShopifyImageURL(raw) {
    if (typeof raw !== 'string' || !raw.trim()) return null;
    try {
      const url = new URL(raw.trim(), window.location.href);
      if (url.protocol !== 'https:') return null;
      const shopifyCDN = url.hostname === 'cdn.shopify.com' ||
        url.hostname.endsWith('.shopifycdn.net');
      const shopImageProxy = url.origin === window.location.origin &&
        /^\/(?:cdn\/shop\/|s\/files\/)/i.test(url.pathname);
      if (!shopifyCDN && !shopImageProxy) return null;
      return url.href;
    } catch (_) {
      return null;
    }
  }



  // Older "coming soon" studio renders have a warm backdrop baked into
  // the image. CSS alone cannot remove it. Correct *preview only* in-browser.
  const cleanedDrafts = new Map();
  function fixDraftBackground(image, source) {
    if (cleanedDrafts.has(source)) return cleanedDrafts.get(source);
    const result = (async () => {
      const w = image.naturalWidth, h = image.naturalHeight;
      if (!w || !h || w * h > 9000000) return null;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return null;
      ctx.drawImage(image, 0, 0);
      const pixels = ctx.getImageData(0, 0, w, h);
      const d = pixels.data, n = w * h, edges = [];
      function sample(x,y) {
        const i = (y*w+x)*4;
        if (d[i+3] > 245) edges.push([d[i], d[i+1], d[i+2]]);
      }
      for(let p=1;p<=9;p++){
        const x=Math.round((w-1)*p/10),y=Math.round((h-1)*p/10);
        sample(0,y);sample(w-1,y);sample(x,0);sample(x,h-1);
      }
      // Transparent files are already suitable; do not recolour them.
      if(edges.length<12) return null;
      const median=k=>edges.map(a=>a[k]).sort((a,b)=>a-b)[Math.floor(edges.length/2)];
      const r0=median(0),g0=median(1),b0=median(2);
      if(r0<228||g0<215||b0<200||r0-b0>46) return null;
      const seen=new Uint8Array(n),queue=new Uint32Array(n);
      let head=0,tail=0;
      function add(i) {
        if(seen[i])return;
        const k=i*4,r=d[k],g=d[k+1],b=d[k+2];
        const distance=Math.abs(r-r0)+Math.abs(g-g0)+Math.abs(b-b0);
        if(d[k+3]<245||r<r0-32||g<g0-34||b<b0-39||
           distance>90||r-b>r0-b0+19) return;
        seen[i]=1;queue[tail++]=i;
      }
      for(let x=0;x<w;x++){add(x);add((h-1)*w+x);}
      for(let y=1;y<h-1;y++){add(y*w);add(y*w+w-1);}
      while(head<tail){
        const i=queue[head++],x=i%w;
        if(x>0)add(i-1);
        if(x<w-1)add(i+1);
        if(i>=w)add(i-w);
        if(i<n-w)add(i+w);
      }
      if(tail<n*.07)return null;
      for(let i=0;i<tail;i++){
        const k=queue[i]*4;
        d[k]=255;d[k+1]=255;d[k+2]=255;
      }
      ctx.putImageData(pixels,0,0);
      const blob=await new Promise(resolve=>canvas.toBlob(resolve,'image/png'));
      return blob?URL.createObjectURL(blob):null;
    })().catch(()=>null);
    cleanedDrafts.set(source,result);
    return result;
  }

  // White and Vanilla have a pale product silhouette. Flood-filling pale pixels
  // mistakes their uppers for background and erases them. Use the unaltered
  // source image for these tones rather than risking loss of product details.
  function isLightDraftColour(name) {
    return /^(white|vanilla)$/i.test(String(name || '').trim());
  }

  function resetPreview() {
    activeState.token += 1;
    activeState.overlay?.remove();
    if (activeState.root && activeState.initialName !== null) {
      const nameNode = activeState.root.querySelector('.aforda-listing-colourways__current, .aforda-colourway-picker__selected');
      if (nameNode) nameNode.textContent = activeState.initialName;
    }
    activeState.swatch = null;
    activeState.root = null;
    activeState.overlay = null;
    activeState.initialName = null;
  }

  function showPreview(swatch) {
    const root = swatch.closest('product-info.aforda-pdp');
    if (!root) return;
    if (activeState.swatch === swatch) return;
    resetPreview();
    if (swatch.classList.contains('is-current') || swatch.classList.contains('is-selected')) return;
    const src = validShopifyImageURL(swatch.dataset.afordaPreviewSrc);
    if (!src) return;
    const gallery = root.querySelector('.product__media-wrapper media-gallery');
    const activeSlide = gallery?.querySelector('.product__media-list .product__media-item.is-active') ||
      gallery?.querySelector('.product__media-list .product__media-item');
    if (!activeSlide) return;
    const name = swatch.dataset.afordaPreviewName || '';
    const nameNode = root.querySelector('.aforda-listing-colourways__current, .aforda-colourway-picker__selected');
    const token = activeState.token;
    activeState.swatch = swatch;
    activeState.root = root;
    activeState.initialName = nameNode?.textContent ?? null;

    const image = new Image();
    image.decoding = 'async';
    const draft = swatch.classList.contains('is-unavailable');
    const paleDraft = draft && isLightDraftColour(name);
    if (draft && !paleDraft) image.crossOrigin = 'anonymous';
    image.alt = name ? 'Preview of ' + name + ' colourway' : 'Colourway preview';
    image.onload = async () => {
      if (token !== activeState.token || activeState.swatch !== swatch || !swatch.isConnected) return;
      let shownImage = image;
      if (draft && !paleDraft) {
        const cleanURL = await fixDraftBackground(image, src);
        if (cleanURL) {
          const cleanedImage = new Image();
          cleanedImage.decoding = 'async';
          cleanedImage.alt = image.alt;
          cleanedImage.src = cleanURL;
          try { await cleanedImage.decode(); shownImage = cleanedImage; } catch (_) {}
        }
      }
      if (token !== activeState.token || activeState.swatch !== swatch || !swatch.isConnected) return;
      const latestSlide = root.querySelector('.product__media-wrapper .product__media-list .product__media-item.is-active') ||
        root.querySelector('.product__media-wrapper .product__media-list .product__media-item');
      if (!latestSlide) return;
      const overlay = document.createElement('div');
      overlay.className = 'aforda-pdp__hover-preview';
      overlay.setAttribute('aria-hidden', 'true');
      if (paleDraft) overlay.classList.add('aforda-pdp__hover-preview--pale');
      shownImage.className = 'aforda-pdp__hover-preview-image';
      overlay.appendChild(shownImage);
      latestSlide.appendChild(overlay);
      activeState.overlay = overlay;
      if (nameNode && name) nameNode.textContent = name;
    };
    image.onerror = () => {
      if (token !== activeState.token || activeState.swatch !== swatch) return;
      if (draft && !paleDraft && image.crossOrigin === 'anonymous') {
        // Fallback for a CDN that blocks canvas CORS; never break previews.
        image.crossOrigin = null;
        image.src = src;
      } else resetPreview();
    };
    image.src = src;
  }

  // Small, non-purchasable swatches need the same clean white canvas as the
  // active product swatches. Only swap their in-page thumbnail source;
  // the larger hover preview continues using its own unmodified source.
  function cleanDraftSwatches(scope = document) {
    const selector = '.aforda-listing-colourways__item.is-unavailable .aforda-listing-colourways__image';
    const images = [];
    if (scope.matches?.(selector)) images.push(scope);
    scope.querySelectorAll?.(selector).forEach((node) => images.push(node));

    for (const thumbnail of images) {
      if (thumbnail.dataset.afordaSwatchCleaning) continue;
      thumbnail.dataset.afordaSwatchCleaning = 'pending';

      const swatch = thumbnail.closest('.aforda-listing-colourways__item');
      if (isLightDraftColour(swatch?.dataset.afordaPreviewName)) {
        thumbnail.dataset.afordaSwatchCleaning = 'pale-preserved';
        continue;
      }
      const raw = swatch?.dataset.afordaPreviewSrc || thumbnail.currentSrc || thumbnail.src;
      const url = validShopifyImageURL(raw);
      if (!url) {
        thumbnail.dataset.afordaSwatchCleaning = 'unavailable';
        continue;
      }

      // Shopify CDN resizes these decorative 30px thumbnail assets to
      // 240px before processing; no full-size background segmentation needed.
      const imageURL = new URL(url);
      imageURL.searchParams.set('width', '240');
      const image = new Image();
      image.decoding = 'async';
      image.crossOrigin = 'anonymous';
      image.onload = async () => {
        const clean = await fixDraftBackground(image, imageURL.href);
        if (!thumbnail.isConnected) return;
        if (clean) {
          thumbnail.removeAttribute('srcset');
          thumbnail.src = clean;
          thumbnail.classList.add('aforda-swatch-image--cleaned');
          thumbnail.dataset.afordaSwatchCleaning = 'cleaned';
        } else {
          thumbnail.dataset.afordaSwatchCleaning = 'fallback';
        }
      };
      image.onerror = () => {
        if (thumbnail.isConnected) thumbnail.dataset.afordaSwatchCleaning = 'fallback';
      };
      image.src = imageURL.href;
    }
  }

  function bootSwatchCleaning(event) {
    cleanDraftSwatches(event?.target || document);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootSwatchCleaning, { once: true });
  } else {
    bootSwatchCleaning();
  }
  document.addEventListener('shopify:section:load', bootSwatchCleaning);

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    const swatch = event.target.closest?.(swatchSelector);
    if (swatch) showPreview(swatch);
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    const swatch = event.target.closest?.(swatchSelector);
    if (!swatch || activeState.swatch !== swatch) return;
    if (event.relatedTarget && swatch.contains(event.relatedTarget)) return;
    resetPreview();
  });

  // Support keyboard preview for linked colourways.
  document.addEventListener('focusin', (event) => {
    const swatch = event.target.closest?.(swatchSelector);
    if (swatch) showPreview(swatch);
  });
  document.addEventListener('focusout', (event) => {
    const swatch = event.target.closest?.(swatchSelector);
    if (!swatch || activeState.swatch !== swatch) return;
    if (event.relatedTarget && swatch.contains(event.relatedTarget)) return;
    resetPreview();
  });
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('.product__media-wrapper')) resetPreview();
  });
  document.addEventListener('shopify:section:unload', resetPreview);
})();
